import { h, ref, computed, onMounted } from 'vue';
import { configRegistry } from '@/lib/configRegistry';
import { VChip, VChipGroup, VIcon, VBtn, VTooltip } from 'vuetify/components';
import { getByPath, formatValue, prettyLabel } from './useFieldFormat';

type RendererName =
  | 'userFullName'
  | 'clientName'
  | 'chipBoolean'        // muestra chip SOLO si es truthy
  | 'arrayChips'
  | 'downloadLink'
  | 'chipLabelValue'     // chip “texto: valor” (p.ej. ID)
  | 'text'
  | 'boolean'
  | 'combine'; 

type RendererConfig =
  | RendererName
  | { name: RendererName; props?: Record<string, any> };

function wrapAlign(children: any, align?: 'start'|'center'|'end') {
  const cls =
    align === 'center' ? 'd-flex justify-center' :
    align === 'end'    ? 'd-flex justify-end' :
                         'd-flex justify-start';
  return h('div', { class: cls }, Array.isArray(children) ? children : [children]);
}

/** Importante:
 *  - Tabla: headers NO traen showLabel -> se considera false (no prefijos).
 *  - Cards: fields SÍ traen showLabel (true/false) -> se respeta.
 */
const wantsLabel = (header: any) => Boolean(header?.showLabel);

export function useCellRenderers(moduleName: string) {
  const moduleRenderers = ref<Record<string, RendererConfig>>({});

  onMounted(async () => {
    const loader = (configRegistry as any)[moduleName];
    if (!loader) return;
    const mod = await loader();
    if (mod?.RENDERERS) moduleRenderers.value = mod.RENDERERS;
  });

  const builtins: Record<string, (ctx: {
    item:any, header:any, value:any
  }) => any> = {
    // ====== nombre completo de usuario ======
    userFullName: ({ item, header }) => {
      const u =
        getByPath(item, header.value) ||
        item?.attributes?.user ||
        item?.user;
      const full = [u?.name, u?.last_name ?? u?.last].filter(Boolean).join(' ') || '—';
      return wrapAlign(h('span', full), header.align);
    },

    // ====== nombre de cliente ======
    clientName: ({ item, header }) => {
      const src = getByPath(item, header.value) ?? item?.client;
      const a = src?.attributes || {};
      const name = a.commercial_name || a.legal_name || a.name || '—';
      return wrapAlign(h('span', { style:'cursor:pointer' }, name), header.align);
    },

    // ====== chip boolean (ej: Autorización) ======
    chipBoolean: ({ item, header }) => {
      const v = !!getByPath(item, header.value);
      if (!v) return null;
      const props = (header?.renderer as any)?.props || {};
      const label =
        props.label ||
        (String(header.value || '').toLowerCase().includes('autoriz') ? 'Requiere Autorización' : (header.title || prettyLabel(header.value)));
      const color = props.color || 'orange';
      return wrapAlign(h(VChip, { size:'x-small', color, label:true }, () => label), header.align);
    },

    // ====== chip “texto: valor” (ID) ======
    chipLabelValue: ({ value, header }) => {
      const label = header.title || prettyLabel(header.value);
      const showLabel = wantsLabel(header); // <- default false
      const txt = showLabel ? `${label}: ${formatValue(value, header.format)}` : `${formatValue(value, header.format)}`;
      return wrapAlign(h(VChip, { label:true, size:'x-small', variant:'tonal' }, () => txt), header.align);
    },

    // ====== chips para arrays ======
    arrayChips: ({ value, header }) => {
      const arr = Array.isArray(value) ? value : [];
      const vnode = h(VChipGroup, null,
        () => arr.map((x:any, i:number) =>
          h(VChip, { size:'small', label:true, key:i }, () =>
            x?.attributes?.commercial_name || x?.attributes?.legal_name || x?.attributes?.name || x?.name || String(x)
          )
        )
      );
      return wrapAlign(vnode, header.align);
    },

    // ====== descarga (uno o varios) ======
    downloadLink: ({ value, header }) => {
      const extractAll = (v:any): string[] => {
        if (!v) return [];
        if (typeof v === 'string') return [v];
        if (Array.isArray(v)) return v.flatMap(extractAll);
        if (typeof v === 'object') {
          const u = v.url || v.path || v.file;
          return u ? [u] : [];
        }
        return [];
      };
      const urls = extractAll(value);
      if (!urls.length) return null;

      const baseLabel = (header?.renderer as any)?.props?.label || header.title || prettyLabel(header.value);
      const showLabel = wantsLabel(header); // <- default false en tabla, true/false en cards
      const iconName = header.icon || 'mdi-download';

      const filename = (u:string) => {
        try {
          const clean = decodeURIComponent(u.split('?')[0]);
          return clean.substring(clean.lastIndexOf('/') + 1) || '';
        } catch { return ''; }
      };

      const nodes = urls.map((u, i) =>
        h(VTooltip, { key: `${u}-${i}`, text: `${baseLabel}${filename(u) ? ' · ' + filename(u) : ''}`, location:'top' }, {
          activator: ({ props }: any) =>
            h(VBtn, {
              ...props,
              href:u, target:'_blank', rel:'noopener',
              variant:'text', size:'small',
              icon: !showLabel, // sin etiqueta -> solo icono
              class:'px-2',
              'aria-label': `${baseLabel} ${filename(u)}`
            }, () => showLabel
                ? [ h(VIcon, { size:18, class:'mr-1' }, { default: () => iconName }), baseLabel ]
                : [ h(VIcon, { size:18 }, { default: () => iconName }) ]
            )
        })
      );

      return wrapAlign(h('div', { class:'d-flex align-center gap-1' }, nodes), header.align);
    },

    // ====== texto / moneda / número / % / fecha ======
    text: ({ value, header }) => {
      const showLabel = wantsLabel(header); // <- default false
      const iconName = header.icon;
      const label = header.title || prettyLabel(header.value);
      const content = [
        iconName ? h(VIcon, { size:16, class:'mr-1' }, { default: () => iconName }) : null,
        showLabel ? h('strong', `${label}: `) : null,
        h('span', formatValue(value, header.format)),
      ];
      return wrapAlign(h('div', { class:'d-inline-flex align-center' }, content), header.align);
    },

    // ====== condicional (icono check o close) ======
    boolean: ({ value, header }) => {
      // Props opcionales por si quieres personalizar
      const props = (header?.renderer as any)?.props || {};
      const {
        trueIcon    = 'mdi-check-circle',
        falseIcon   = 'mdi-close',
        pendingIcon = 'mdi-clock-outline',

        trueLabel    = 'Sí',
        falseLabel   = 'No',
        pendingLabel = 'Pendiente',

        showLabel = false,
      } = props;
      
      const isPending = value === null || value === undefined || value === '';
      const isTrue =
        value === true ||
        value === 1 ||
        value === '1' ||
        (typeof value === 'string' && value.toLowerCase() === 'true');
      const isFalse = !isPending && !isTrue;

      const icon  = isTrue ? trueIcon : isPending ? pendingIcon : falseIcon;
      const label = isTrue ? trueLabel : isPending ? pendingLabel : falseLabel;

      // Con tooltip y opción de mostrar texto
      const iconVNode = h(VIcon, { size: 18, class: showLabel ? 'mr-1' : undefined }, { default: () => icon });
      const content = showLabel ? h('span', label) : null;

      const withTooltip = h(
        VTooltip,
        { text: label, location: 'top' },
        {
          activator: ({ props: ttProps }: any) =>
            h('span', { ...ttProps, class: 'd-inline-flex align-center' }, [iconVNode, content]),
        }
      );

      return wrapAlign(withTooltip, header.align);
    },

    combine: ({ item, header }) => {
      // props esperadas:
      // - paths: string[] con los keypaths a concatenar
      // - separator: string, por default ' '
      // - fallback: string, por default '—'
      const props = (header?.renderer as any)?.props || {};
      const paths: string[] = Array.isArray(props.paths) ? props.paths : [];
      const separator: string = props.separator ?? ' ';
      const fallback: string = props.fallback ?? '—';

      const values = paths
        .map(p => getByPath(item, p) ?? getByPath(item?.attributes, p) ?? (item as any)?.[p])
        .filter(v => v !== null && v !== undefined && String(v).trim() !== '')
        .map(v => String(v).trim());

      const text = values.length ? values.join(separator) : fallback;
      return wrapAlign(h('span', text), header.align);
    },
  };

  function inferRendererByFormat(header:any): keyof typeof builtins {
    if (header.format === 'download') return 'downloadLink';
    if (header.format === 'chip') {
      const key = String(header.value || '').toLowerCase();
      if (key.includes('autoriz')) return 'chipBoolean';
      return 'chipLabelValue'; // ID y similares
    }
    return 'text';
  }

  function renderCell(item:any, header:any) {
    const value =
      getByPath(item, header.value) ??
      item?.attributes?.[header.value] ??
      (item as any)?.[header.value];

    // renderer forzado por el módulo
    const def = moduleRenderers.value?.[header.value];
    const rName = typeof def === 'string' ? def : (def as any)?.name;
    const forced = rName ? builtins[rName] : null;
    const headerWithRenderer = { ...header, renderer: def };

    if (forced) {
      return forced({ item, header: headerWithRenderer, value });
    }

    // fallback por formato
    const fallback = builtins[inferRendererByFormat(header)] || builtins.text;

    // si el valor es objeto y no hay renderer explícito, saca algo human-readable
    const plain =
      (value?.attributes?.commercial_name || value?.attributes?.legal_name || value?.attributes?.name) ??
      ([value?.name, value?.last_name ?? value?.last].filter(Boolean).join(' ') || undefined);
    const valueToUse = (typeof value === 'object' && plain) ? plain : value;

    return fallback({ item, header: headerWithRenderer, value: valueToUse });
  }

  return { renderCell, moduleRenderers: computed(() => moduleRenderers.value) };
}
